import { faChevronDown, faChevronUp, faHashtag, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Placeholder, Row, Tooltip } from "react-bootstrap";
import { EXPLAIN_RISK, SCAN_MULTI } from "../../../../amplify/appsync/write";
import { GenerateModuleHeroV2 } from "../../../common/GenerateModuleHeroV2";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { isSectionUnread } from "../utils";
import { SectionRiskButton } from "./SectionRiskButton";
import { SectionRiskDetails } from "./SectionRiskDetails";
import { dashboardLLMCache } from "../cache";
import { ModuleErrorDisplay } from "../common/ModuleErrorDisplay";

export const SectionRisk = ({
    isLoading,
    data,
    tenant,
    riskActive,
    setRiskActive,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    setRegenerateTarget,
    setShowContextMenu,
    handleShowScanMultiModal,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);
        const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_RISK]) {
            const version = latestFileVersions[EXPLAIN_RISK];

            // console.log("[Patient Summary] Loading dashboardLLMCache with version ", version.source_version_id);
            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
            .then((data: any) => {
                // console.log("[Patient Summary] Found citation_mapping", data.citation_mapping);
                setScanMulti(data);
            })
        }
    }, [latestFileVersions]);

    const cleanTitles = (title: string) => {
        return title.replaceAll("_", " ");
    }

    return (
        <Card className="mt-4">
            <FakeLoadingBar isActive={isLoading} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHashtag} fixedWidth /></span><strong>Outcome Risk Assessments</strong> {isSectionUnread(EXPLAIN_RISK, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_RISK} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_RISK); setShowContextMenu(true)}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        {
                            isLoading && (
                                <Row className="mt-2">
                                    <Col md={12}>
                                        <Placeholder animation="glow">
                                            <Placeholder xs={4} />
                                        </Placeholder>
                                    </Col>
                                </Row>
                            )
                        }
                        {data && data["error"] ? (
                            <Row className="mt-2">
                                <Col md={12}>
                                    <ModuleErrorDisplay
                                        errorMessage={data["error"]}
                                        moduleTitle="Outcome Risk Assessments"
                                        hasPreviousVersions={!!(latestFileVersions && latestFileVersions[EXPLAIN_RISK])}
                                    />
                                </Col>
                            </Row>
                        ) : data && data.risk_assessment ? (
                            <Row className="mt-2">
                                <Col md={6}>
                                    {
                                        data.risk_assessment.map((item: any, idx: number) => {
                                            if (item && Object.keys(item).length > 0) {
                                                return <SectionRiskButton idx={idx} isLoading={false} active={riskActive} setActive={setRiskActive} data={item} title={cleanTitles(item.outcome)} scanMulti={scanMulti} />
                                            } else {
                                                return <></>
                                            }
                                        })
                                    }
                                </Col>
                                <Col md={6}>
                                    {
                                        data && data.risk_assessment[riskActive] && (
                                            <SectionRiskDetails data={data.risk_assessment[riskActive]} title={cleanTitles(data.risk_assessment[riskActive].outcome)} feedbacks={feedbacks} setFeedbacks={setFeedbacks} patientVisit={patientVisit} scanMulti={scanMulti} />
                                        )
                                    }
                                </Col>
                            </Row>
                        ) : (
                            <Row className="mt-2">
                                <Col md={12}>
                                    <>No Data</>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
